
// packages/components/src/agent-management/ModelSelectorCard.tsx
import React, { useEffect, useState, useMemo } from 'react';
import { Card, SelectField, Spinner, Alert, Tooltip } from '@dopomogai/ui';
import { useAgent } from '@dopomogai/agent-kit';
import { modelService, isApiError } from '@dopomogai/api-client';
import type { AvailableLLM } from '@dopomogai/types';
import { Brain, Lock } from 'lucide-react';
import { toast } from '@dopomogai/ui';

interface ModelSelectorCardProps {
  agentId: string;
  readOnly?: boolean;
}

function shortModelName(id: string, name?: string, provider?: string): string {
  // Prefer last path segment in id (often the model id), strip :free
  if (id && id.includes('/')) {
    const seg = id.split('/').pop() || id;
    return seg.replace(/:free$/i, '');
  }
  // If no slash in id, and provider is azure and name is "Azure deployment 'xxx'", extract xxx
  if (provider && provider.toLowerCase() === 'azure' && name && /Azure deployment/i.test(name)) {
    const m = name.match(/'([^']+)'/);
    if (m && m[1]) return m[1];
  }
  return (name || id || '').replace(/:free$/i, '');
}

function niceProvider(m: AvailableLLM): string {
  const p = (m.provider || '').toLowerCase();
  const id = (m.id || '').toLowerCase();

  // Azure -> OpenAI branding for clarity
  if (p === 'azure') return 'OpenAI';

  // If openrouter or similar aggregator, infer from path segments
  const segments = (m.id || '').split('/');
  const all = segments.map(s => s.toLowerCase());

  const known = ['openai', 'deepseek', 'qwen', 'moonshotai', 'gemini', 'google', 'anthropic'];
  const found = all.find(s => known.includes(s));
  if (found) {
    if (found === 'moonshotai') return 'Moonshot';
    if (found === 'google' || found === 'gemini') return 'Gemini';
    return found.charAt(0).toUpperCase() + found.slice(1);
  }

  // Fallback to provider field
  if (p.includes('openai')) return 'OpenAI';
  if (p.includes('deepseek')) return 'DeepSeek';
  if (p.includes('qwen')) return 'Qwen';
  if (p.includes('moonshot')) return 'Moonshot';
  if (p.includes('gemini') || p.includes('google')) return 'Gemini';

  if (m.provider) {
    return m.provider.charAt(0).toUpperCase() + m.provider.slice(1);
  }
  return 'Model';
}

export const ModelSelectorCard: React.FC<ModelSelectorCardProps> = ({ agentId, readOnly = false }) => {
  const { agent, isLoading: agentLoading, error: agentError, updateAgent } = useAgent(agentId);

  const [models, setModels] = useState<AvailableLLM[]>([]);
  const [isModelsLoading, setIsModelsLoading] = useState(true);
  const [modelsError, setModelsError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const loadModels = async () => {
      setIsModelsLoading(true);
      setModelsError(null);
      try {
        const response = await modelService.getAvailableModels();
        if (cancelled) return;
        if (isApiError(response)) {
          setModelsError(response.message || 'Could not load models.');
        } else {
          setModels(response);
        }
      } catch (err: any) {
        if (!cancelled) setModelsError(err?.message || 'Could not load models.');
      } finally {
        if (!cancelled) setIsModelsLoading(false);
      }
    };
    loadModels();
    return () => {
      cancelled = true;
    };
  }, []);

  const selectedModel = agent?.llm_model || '';

  const options = useMemo(
    () =>
      models.map((m) => {
        const prov = niceProvider(m);
        const display = shortModelName(m.id || '', m.name, m.provider || '');
        const isFree = /:free$/i.test(m.id || '');
        const label = `${prov} — ${display}${isFree ? ' 🎁 Free' : ''}`;
        return { value: m.id, label };
      }),
    [models]
  );

  const handleModelChange = async (value: string | number) => {
    if (readOnly) return;
    if (!agent || !value || value === selectedModel) return;
    setIsSaving(true);
    try {
      await updateAgent({ llm_model: String(value) });
      toast.success('Model updated successfully!');
    } catch (err: any) {
      toast.error(err?.message || 'Failed to update model.');
    } finally {
      setIsSaving(false);
    }
  };

  const currentModel = useMemo(() => {
    return models.find(m => m.id === agent?.llm_model);
  }, [models, agent?.llm_model]);

  const pricingBlock = useMemo(() => {
    if (!currentModel) return null;
    const isFree = /:free$/i.test(currentModel.id || '');
    const inpPM = currentModel.input_pricing_per_million_tokens ?? 0;
    const outPM = currentModel.output_pricing_per_million_tokens ?? 0;
    const inpPer1K = inpPM / 1000;
    const outPer1K = outPM / 1000;

    const hasPricing = (inpPer1K > 0) || (outPer1K > 0);
    if (!hasPricing) {
      if (isFree) {
        return (
          <div className="text-xs text-slate-500">
            This model is free but rate-limited; responses may be unstable.
          </div>
        );
      }
      return null;
    }

    const exampleCost = (2 * inpPer1K + 1 * outPer1K);
    return (
      <div className="text-xs text-slate-500">
        ~${inpPer1K.toFixed(4)} per 1k input tokens • ~${outPer1K.toFixed(4)} per 1k output tokens
        <div>Example: ~${exampleCost.toFixed(4)} for a 2k-in / 1k-out turn</div>
      </div>
    );
  }, [currentModel]);

  return (
    <Card
      title="Model"
      icon={<Brain size={18} />}
      headerBgColor="bg-indigo-100"
      className="h-full"
      actionButton={readOnly ? <Tooltip text="View-only"><Lock size={16} className="text-slate-500" /></Tooltip> : undefined}
    >
      {agentLoading ? (
        <div className="flex items-center justify-center py-6">
          <Spinner text="Loading agent…" />
        </div>
      ) : agentError || !agent ? (
        <Alert
          type="error"
          title="Unable to load agent"
          message={agentError?.message || 'An unexpected error occurred.'}
        />
      ) : (
        <div className="space-y-3">
          <p className="text-sm text-slate-600">
            Choose the language model that powers this agent. Changes take effect immediately.
          </p>

          {isModelsLoading ? (
            <Spinner text="Fetching models…" />
          ) : modelsError ? (
            <Alert type="error" message={modelsError} />
          ) : (
            <SelectField
              label="Language model"
              name="agent-model"
              value={selectedModel}
              options={options}
              onChange={handleModelChange}
              disabled={isSaving || readOnly}
              placeholder="Select a model"
            />
          )}

          {pricingBlock}
        </div>
      )}
    </Card>
  );
};